import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, Bot, MessageSquare, Send, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../supabase';

/**
 * IA Asistente - Gastify
 * - Embebido por defecto (sidebar -> /sistema/chatbot)
 * - Llama a Edge Function: chat-gastify
 * - Hardening: Authorization header + parsing detallado de errores (500/401/etc.)
 */
const Chatbot = ({ mode = 'embedded' }) => {
  const isEmbedded = mode === 'embedded';
  const [isOpen, setIsOpen] = useState(isEmbedded);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: 'Hola, soy el asistente tecnico de Gastify. ¿En que modulo o proceso necesitas ayuda hoy?',
      sender: 'bot',
    },
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [lastError, setLastError] = useState(null);

  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    if (isEmbedded) setIsOpen(true);
  }, [isEmbedded]);

  const extractFunctionsError = async (error) => {
    try {
      if (error?.context && typeof error.context.json === 'function') {
        const json = await error.context.json().catch(() => null);
        return json ? JSON.stringify(json) : null;
      }

      if (typeof error?.message === 'string' && error.message.trim()) {
        return error.message;
      }

      return null;
    } catch {
      return null;
    }
  };

  const renderMessage = (text, sender) => {
    if (!text) return null;

    return text.split(/(\*\*.*?\*\*)/g).map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <span
            key={`${sender}-part-${index}`}
            className={`font-black ${sender === 'bot' ? 'text-[var(--gst-graphite-900)]' : 'text-white'}`}
          >
            {part.slice(2, -2)}
          </span>
        );
      }

      return part;
    });
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    if (!newMessage.trim() || isTyping) return;

    const userText = newMessage.trim();
    setMessages((currentMessages) => [
      ...currentMessages,
      { id: Date.now(), text: userText, sender: 'user' },
    ]);
    setNewMessage('');
    setIsTyping(true);
    setLastError(null);

    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) console.warn('getSession error:', sessionError);

      const accessToken = sessionData?.session?.access_token;
      const { data, error } = await supabase.functions.invoke('chat-gastify', {
        body: { message: userText },
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      });

      if (error) throw error;

      const reply = data?.reply || data?.message || data?.text;
      setMessages((currentMessages) => [
        ...currentMessages,
        {
          id: Date.now() + 1,
          text: reply || 'No recibi una respuesta valida del servidor.',
          sender: 'bot',
        },
      ]);
    } catch (error) {
      console.error('Error en Chatbot:', error);

      const detail = await extractFunctionsError(error);
      const statusHint =
        (error?.context?.status ? `HTTP ${error.context.status}` : null) ||
        (error?.status ? `HTTP ${error.status}` : null);
      const finalDetail = detail || statusHint || 'Sin detalle adicional';

      setLastError({ raw: error, detail: finalDetail });
      toast.error('La IA no pudo responder.');

      setMessages((currentMessages) => [
        ...currentMessages,
        {
          id: Date.now() + 1,
          sender: 'bot',
          text:
            `No pude procesar la consulta porque el servicio devolvio un error.\n` +
            `• Diagnostico: ${finalDetail}\n` +
            `• Accion: Revisar logs de la Edge Function "chat-gastify" en Supabase.`,
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const statusBadge = lastError ? (
    <div className="flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[9px] font-black uppercase tracking-widest text-rose-700">
      <AlertCircle size={14} />
      Error
    </div>
  ) : (
    <div className="flex items-center gap-2 rounded-md border border-[color:var(--color-accent)] bg-[var(--color-accent-soft)] px-3 py-2 text-[9px] font-black uppercase tracking-widest text-[var(--color-accent-dark)]">
      <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-accent-dark)] animate-pulse" />
      Online
    </div>
  );

  const panel = (
    <div className="flex h-full flex-col overflow-hidden rounded-[1.5rem] border border-[color:var(--gst-border)] bg-[var(--surface)] shadow-[var(--gst-shadow-md)]">
      <div className="flex items-center justify-between border-b border-[color:var(--gst-border)] bg-[var(--gst-cream-50)] px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[var(--gst-mint-100)] text-[var(--gst-mint-700)]">
            <Bot size={20} />
          </div>
          <div>
            <h3 className="text-sm font-black tracking-tight text-[var(--gst-graphite-900)]">
              Asistente Gastify
            </h3>
            <div className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--gst-mint-500)] animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--gst-mint-700)]">
                Gastify AI
              </span>
            </div>
          </div>
        </div>
        {!isEmbedded ? (
          <button
            onClick={() => setIsOpen(false)}
            className="rounded-md p-2 text-[var(--gst-graphite-500)] transition hover:bg-[var(--surface)] hover:text-[var(--gst-graphite-900)]"
            type="button"
          >
            <X size={18} />
          </button>
        ) : null}
      </div>

      <div className="flex items-center justify-between gap-3 border-b border-[color:var(--gst-border)] bg-[var(--surface)] px-5 py-3">
        <div>
          <h1 className="text-lg font-black uppercase tracking-tight text-[var(--gst-graphite-900)] md:text-xl">
            IA Asistente
          </h1>
          <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--gst-graphite-500)]">
            Soporte operativo (Edge Function: chat-gastify)
          </p>
        </div>
        {statusBadge}
      </div>

      <div className="custom-scrollbar flex-1 space-y-4 overflow-y-auto bg-[var(--gst-cream-50)]/70 p-5">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl p-4 text-sm ${
                message.sender === 'user'
                  ? 'rounded-tr-md bg-[var(--gst-mint-500)] text-white shadow-[var(--gst-shadow-sm)]'
                  : 'rounded-tl-md border border-[color:var(--gst-border)] bg-[var(--surface)] text-[var(--gst-graphite-700)] shadow-[var(--gst-shadow-sm)]'
              }`}
            >
              <div className="flex flex-col gap-1">
                <span className="mb-1 text-[10px] font-black uppercase tracking-widest opacity-60">
                  {message.sender === 'user' ? 'Tu' : 'Gastify Bot'}
                </span>
                <p className="whitespace-pre-wrap leading-relaxed">
                  {renderMessage(message.text, message.sender)}
                </p>
              </div>
            </div>
          </div>
        ))}

        {isTyping ? (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-tl-md border border-[color:var(--gst-border)] bg-[var(--surface)] p-4 shadow-[var(--gst-shadow-sm)]">
              <div className="flex gap-1">
                <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-[var(--gst-mint-400)]" />
                <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-[var(--gst-mint-400)] [animation-delay:0.2s]" />
                <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-[var(--gst-mint-400)] [animation-delay:0.4s]" />
              </div>
            </div>
          </div>
        ) : null}

        <div ref={messagesEndRef} />
      </div>

      <div className="border-t border-[color:var(--gst-border)] bg-[var(--surface)] p-4 md:p-5">
        <form onSubmit={handleSendMessage} className="relative">
          <input
            type="text"
            value={newMessage}
            onChange={(event) => setNewMessage(event.target.value)}
            placeholder="Pregunta sobre clientes, caja, stock o configuraciones..."
            className="w-full rounded-md border border-[color:var(--gst-border)] bg-[var(--gst-cream-50)] p-4 pr-16 text-sm font-semibold text-[var(--gst-graphite-900)] outline-none transition focus:border-[var(--gst-mint-500)]"
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || isTyping}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md bg-[var(--gst-mint-500)] p-2.5 text-white transition hover:bg-[var(--gst-mint-600)] disabled:opacity-30"
          >
            <Send size={18} />
          </button>
        </form>

        {isEmbedded ? (
          <p className="mt-4 text-center text-[8px] font-bold uppercase tracking-[0.2em] text-[var(--gst-graphite-300)]">
            Gastify AI • Soporte tecnico de gestion
          </p>
        ) : null}
      </div>
    </div>
  );

  if (isEmbedded) {
    return (
      <div className="h-full w-full animate-nova-in p-5 md:p-6">
        <div style={{ height: 'calc(100vh - 190px)' }}>
          {panel}
        </div>

        <style>{`
          .custom-scrollbar::-webkit-scrollbar { width: 4px; }
          .custom-scrollbar::-webkit-scrollbar-thumb { background: #d7ddd9; border-radius: 20px; }
          .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
          @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>
      </div>
    );
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="group fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-md bg-[var(--gst-mint-500)] p-4 text-white shadow-[var(--gst-shadow-lg)] transition hover:scale-105"
        type="button"
      >
        <div className="relative">
          <MessageSquare size={24} />
          <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full border-2 border-[var(--gst-mint-500)] bg-emerald-400 animate-pulse" />
        </div>
        <span className="pr-1 text-sm font-bold">Asistente Gastify</span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 h-[600px] w-[400px] max-w-[92vw]">
      {panel}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #d7ddd9; border-radius: 20px; }
      `}</style>
    </div>
  );
};

export default Chatbot;
